import { useEffect, useState } from "react";
import { Loader2, Mail, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtDate, fmtErr } from "../api";

// Admin → Users: sign-in access view over the same central employee master.
// Focused on access (email / role / active / invites); HR fields live in HRMS → Employee Master.
const Users = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [inviting, setInviting] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await staffApi.get("/users");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try {
      await staffApi.patch(`/users/${u.id}`, { active: !(u.active !== false) });
      toast.success(`${u.name} ${u.active === false ? "activated" : "deactivated"}.`);
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const invite = async (u) => {
    setInviting(u.id);
    try {
      await staffApi.post(`/users/${u.id}/invite`, { resend: true });
      toast.success(`Invite sent to ${u.email}.`);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setInviting(null);
    }
  };

  const filtered = rows.filter((u) =>
    `${u.name} ${u.email} ${u.role} ${u.employee_code}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-users">
      <h1 className="font-display text-2xl font-bold text-slate-900">Users & Access</h1>
      <p className="mt-1 text-sm text-slate-500">Sign-in access for GR CONNECT and the mobile app. HR details are managed in HRMS → Employee Master.</p>

      <div className="mt-6 relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input type="text" placeholder="Search name, email, role…" value={q} data-testid="users-search"
          onChange={(e) => setQ(e.target.value)}
          className="w-full border border-slate-300 pl-9 pr-3 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" />
      </div>

      {loading ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="users-error">{error}</p>
      ) : (
        <div className="mt-5 bg-white border border-slate-200 overflow-x-auto" data-testid="users-table">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                {["User", "Role", "Portal Access", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`users-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">{u.name}</p>
                    <p className="text-xs text-slate-400">{u.employee_code || "—"} · joined {fmtDate(u.date_of_joining)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-slate-700">
                      {(u.role === "SuperAdmin" || u.role === "Admin") && <ShieldCheck size={12} className="text-[#C5221F]" />}
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.email || <span className="text-slate-400">no email — cannot sign in</span>}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${u.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`} data-testid={`users-status-${u.id}`}>
                      {u.active !== false ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      {u.email && (
                        <button onClick={() => invite(u)} disabled={inviting === u.id} data-testid={`users-invite-${u.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#C5221F] hover:underline disabled:opacity-50">
                          {inviting === u.id ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />} Invite
                        </button>
                      )}
                      <button onClick={() => toggleActive(u)} data-testid={`users-toggle-${u.id}`} className="text-[11px] font-semibold text-slate-500 hover:underline">
                        {u.active !== false ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500" data-testid="users-empty">No users match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Users;
